import { useEffect, useState, type FormEvent } from "react";
import { fetchDelegation, type DelegationReport } from "../lib/delegation";

const STATUS_LABEL: Record<string, string> = {
  ok: "answered",
  "no-address": "no address",
  unreachable: "no TCP reply",
  "no-soa": "no SOA",
  "not-probeable": "not probeable",
};

export function DelegationCheck() {
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<DelegationReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (raw: string) => {
    const d = normalizeDomain(raw);
    if (!d) return;
    setDomain(d);
    setBusy(true);
    setReport(null);
    setError(null);
    history.replaceState(null, "", `?${new URLSearchParams({ domain: d })}`);
    fetchDelegation(d)
      .then((r) => setReport(r))
      .catch((e) => setError(String(e?.message ?? e)))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    const d = new URLSearchParams(window.location.search).get("domain");
    if (d) run(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    run(domain);
  };

  // Only compare the two NS sets when we actually have both.
  const diff = report && report.parentNs ? nsDiff(report.parentNs, report.childNs) : null;

  return (
    <main className="console">
      <div className="input-row" role="search">
        <span className="input-row-label">DELEGATION</span>
        <form onSubmit={submit} className="lookup-form">
          <input
            type="text"
            placeholder="example.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            autoFocus
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-label="Domain name"
          />
          <button type="submit" disabled={busy || !domain.trim()}>
            {busy ? "Checking…" : "Check"}
          </button>
        </form>
        <span className="input-row-hint" aria-hidden="true">
          <span className="stat-dot" /> registry vs zone · direct SOA probe
        </span>
      </div>

      {error ? <div className="error" role="alert">{error}</div> : null}

      <div className="body-grid body-grid-single">
        <section className="table-panel">
          <div className="panel-title">
            DELEGATION REPORT
            {report ? <b> · {report.domain}</b> : null}
          </div>

          {!report && !busy ? (
            <div className="empty-state">
              <p>
                Checks the authoritative side of DNS, which a normal lookup can't see: whether the
                registry and the zone agree on the nameservers, and whether every nameserver is
                serving the same version of the zone.
              </p>
              <p className="empty-hint">Try <code>google.com</code>, <code>wikipedia.org</code>, or your own domain.</p>
            </div>
          ) : null}

          {busy ? <div className="empty-state"><p>Querying registry and probing nameservers…</p></div> : null}

          {report ? (
            <>
              <ul className="health-findings">
                {report.findings.map((f, i) => (
                  <li key={i} className={`finding finding-${f.level}`}>
                    <span className="finding-icon" aria-hidden="true">
                      {f.level === "good" ? "✓" : f.level === "warn" ? "!" : "·"}
                    </span>
                    {f.text}
                  </li>
                ))}
              </ul>

              <h4 className="deleg-subhead">Registry vs zone</h4>
              <table className="health-table deleg-sets">
                <thead>
                  <tr><th>Source</th><th>Nameservers</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="ht-type">Registry<span className="deleg-note">parent</span></td>
                    <td className="ht-records">
                      {report.parentNs ? (
                        <ul>
                          {report.parentNs.map((n) => (
                            <li key={n}>
                              <code className={diff?.onlyParent.includes(n) ? "deleg-diff" : undefined}>{n}</code>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="ht-empty">unavailable — no RDAP server for this TLD</span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="ht-type">Zone<span className="deleg-note">child</span></td>
                    <td className="ht-records">
                      {report.childNs.length ? (
                        <ul>
                          {report.childNs.map((n) => (
                            <li key={n}>
                              <code className={diff?.onlyChild.includes(n) ? "deleg-diff" : undefined}>{n}</code>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="ht-empty">no NS records returned</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>

              <h4 className="deleg-subhead">Per-nameserver SOA serial</h4>
              <table className="health-table">
                <thead>
                  <tr><th>Nameserver</th><th>Address</th><th>Status</th><th>SOA serial</th></tr>
                </thead>
                <tbody>
                  {report.probes.map((p) => (
                    <tr key={p.host} className={`health-row deleg-${p.status}`}>
                      <td className="ht-type"><code>{p.host}</code></td>
                      <td className="ht-records">
                        {p.ips.length ? <code>{p.ips[0]}</code> : <span className="ht-empty">—</span>}
                      </td>
                      <td className="ht-status" title={p.message}>{STATUS_LABEL[p.status] ?? p.status}</td>
                      <td className="ht-records">
                        {p.serial != null ? <code>{p.serial}</code> : <span className="ht-empty">—</span>}
                      </td>
                    </tr>
                  ))}
                  {report.probes.length === 0 ? (
                    <tr><td colSpan={4} className="ht-empty">No nameservers to probe.</td></tr>
                  ) : null}
                </tbody>
              </table>

              <p className="whois-cross">
                Also check{" "}
                <a href={`/dns-health?domain=${encodeURIComponent(report.domain)}`}>DNS health</a> ·{" "}
                <a href={`/dnssec-check?host=${encodeURIComponent(report.domain)}&type=A`}>DNSSEC validation</a> ·{" "}
                <a href={`/ns-lookup?host=${encodeURIComponent(report.domain)}`}>NS propagation</a>.
              </p>
            </>
          ) : null}
        </section>
      </div>
    </main>
  );
}

function nsDiff(parent: string[], child: string[]) {
  return {
    onlyParent: parent.filter((n) => !child.includes(n)),
    onlyChild: child.filter((n) => !parent.includes(n)),
  };
}

function normalizeDomain(raw: string): string {
  let s = raw.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, "");
  return s;
}
